import { USUAL_LAYOUT_WIDTH } from "@bl/domain";
import { Link } from "@tanstack/react-router";
import { EyeOff } from "lucide-react";
import {
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  useRef,
  useState,
} from "react";
import type { Tank } from "../api";
import {
  capacityLabel,
  clampSpot,
  floorOf,
  freeSpot,
  kindColor,
  type Spot,
  sizeOf,
} from "../tankLayout";

/**
 * The cellar drawn as a floor plan (Tyler, 2026-10-04): each tank a circle where it stands, bigger the
 * more it holds. Editable in Settings, where a tank is dragged onto the floor, around it, or off it
 * again; on Production it shows what each tank holds where it stands. Both draw the floor as many
 * squares across as the brewery zoomed it to (Tyler, 2026-10-05), and mark a tank hidden from the tank
 * schedule.
 */

interface Drag {
  id: string;
  size: number;
  /** Where on the tank it was picked up, in squares from its top left. */
  grab: Spot;
  /** Where its top left is now, in squares, not yet snapped. */
  at: Spot;
  fromTray: boolean;
  moved: boolean;
  start: { x: number; y: number };
}

export function TankFloor({
  tanks,
  width = USUAL_LAYOUT_WIDTH,
  spotOf,
  onPlace,
  caption,
  state,
  linkOf,
}: {
  /** The tanks in the cellar; those with no spot wait in a tray above the floor when editing. */
  tanks: Tank[];
  /** How many squares across the floor is: the layout's zoom. */
  width?: number;
  spotOf: (t: Tank) => Spot | null;
  /**
   * Given, the floor is editable: a tank put somewhere, or taken off it with null; `nudge` when an
   * arrow key moved it a square, so more may follow.
   */
  onPlace?: (t: Tank, spot: Spot | null, nudge?: boolean) => void;
  caption?: (t: Tank) => ReactNode;
  /** How full it is drawn: filled with beer in it, ringed when only booked. */
  state?: (t: Tank) => "in" | "booked" | "empty";
  /** A tank holding beer opens that beer's brew. */
  linkOf?: (t: Tank) => string | null;
}) {
  const floor = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const editable = Boolean(onPlace);
  const room = floorOf(width);
  const placed = tanks.flatMap((t) => {
    const spot = spotOf(t);
    const size = sizeOf(t.capacityLiters);
    // Kept on the floor as drawn, should a place ever be off it.
    return spot ? [{ tank: t, spot: clampSpot(spot, size, room), size }] : [];
  });
  const waiting = tanks.filter((t) => !spotOf(t));

  /** The pointer's place on the floor, in squares. */
  const squaresAt = (clientX: number, clientY: number) => {
    const r = floor.current?.getBoundingClientRect();
    if (!r || r.width === 0) return null;
    return {
      x: ((clientX - r.left) / r.width) * room.cols,
      y: ((clientY - r.top) / r.height) * room.rows,
      inside: clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom,
    };
  };

  const pickUp = (t: Tank, fromTray: boolean) => (e: PointerEvent<HTMLElement>) => {
    if (!onPlace || e.button !== 0) return;
    const size = sizeOf(t.capacityLiters);
    const at = squaresAt(e.clientX, e.clientY);
    const spot = spotOf(t);
    const grab =
      spot && at && !fromTray
        ? { x: at.x - spot.x, y: at.y - spot.y }
        : { x: size / 2, y: size / 2 };
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({
      id: t.id,
      size,
      grab,
      at: spot ?? { x: (at?.x ?? 0) - grab.x, y: (at?.y ?? 0) - grab.y },
      fromTray,
      moved: false,
      start: { x: e.clientX, y: e.clientY },
    });
  };

  const carry = (e: PointerEvent<HTMLElement>) => {
    if (!drag) return;
    const at = squaresAt(e.clientX, e.clientY);
    if (!at) return;
    const moved = drag.moved || Math.hypot(e.clientX - drag.start.x, e.clientY - drag.start.y) > 4;
    setDrag({ ...drag, moved, at: { x: at.x - drag.grab.x, y: at.y - drag.grab.y } });
  };

  const putDown = (t: Tank) => (e: PointerEvent<HTMLElement>) => {
    if (!drag || !onPlace) return;
    setDrag(null);
    const at = squaresAt(e.clientX, e.clientY);
    if (!drag.moved) {
      // A tap on a waiting tank puts it in the first free spot.
      if (drag.fromTray) onPlace(t, freeSpot(drag.size, placed, room));
      return;
    }
    if (at?.inside) onPlace(t, clampSpot(drag.at, drag.size, room));
    else if (!drag.fromTray) onPlace(t, null);
  };

  const nudge = (t: Tank, spot: Spot) => (e: KeyboardEvent) => {
    if (!onPlace) return;
    const step: Record<string, Spot> = {
      ArrowLeft: { x: -1, y: 0 },
      ArrowRight: { x: 1, y: 0 },
      ArrowUp: { x: 0, y: -1 },
      ArrowDown: { x: 0, y: 1 },
    };
    const d = step[e.key];
    if (d) {
      e.preventDefault();
      onPlace(
        t,
        clampSpot({ x: spot.x + d.x, y: spot.y + d.y }, sizeOf(t.capacityLiters), room),
        true,
      );
    } else if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      onPlace(t, null);
    }
  };

  const style = (t: Tank, spot: Spot, size: number): CSSProperties =>
    ({
      left: `${(spot.x / room.cols) * 100}%`,
      top: `${(spot.y / room.rows) * 100}%`,
      width: `${(size / room.cols) * 100}%`,
      "--tank": kindColor(t.kind),
    }) as CSSProperties;

  const face = (t: Tank) => (
    <>
      <span className="tank-name">
        {t.hidden ? <EyeOff className="tank-hidden-mark" aria-hidden /> : null}
        {t.name}
      </span>
      <span className="tank-sub">{caption ? caption(t) : capacityLabel(t)}</span>
    </>
  );

  const classOf = (t: Tank, extra = "") =>
    [
      "tank-token",
      state ? `tank-${state(t)}` : "",
      t.outOfService ? "out-of-service" : "",
      t.hidden ? "hidden-tank" : "",
      extra,
    ]
      .filter(Boolean)
      .join(" ");

  const dragged = drag ? tanks.find((t) => t.id === drag.id) : undefined;

  return (
    <div className="tank-floor-wrap">
      {editable && waiting.length ? (
        <div className="tank-tray">
          <span className="faint">Not placed yet. Drag one onto the floor, or tap it.</span>
          <div className="tank-tray-tanks">
            {waiting.map((t) => (
              <button
                key={t.id}
                type="button"
                className={classOf(t, drag?.id === t.id ? "lifted" : "")}
                style={{ "--tank": kindColor(t.kind) } as CSSProperties}
                aria-label={`${t.name}, ${capacityLabel(t)}: put it on the floor`}
                onPointerDown={pickUp(t, true)}
                onPointerMove={carry}
                onPointerUp={putDown(t)}
                onPointerCancel={() => setDrag(null)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onPlace?.(t, freeSpot(sizeOf(t.capacityLiters), placed, room));
                  }
                }}
              >
                {face(t)}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      <div
        ref={floor}
        className={`tank-floor ${editable ? "editable" : ""}`}
        style={
          {
            aspectRatio: `${room.cols} / ${room.rows}`,
            "--cols": room.cols,
            "--rows": room.rows,
          } as CSSProperties
        }
      >
        {placed.map(({ tank: t, spot, size }) => {
          const here = drag?.id === t.id && !drag.fromTray ? drag.at : spot;
          if (editable) {
            return (
              <button
                key={t.id}
                type="button"
                className={classOf(t, drag?.id === t.id ? "lifted" : "")}
                style={style(t, here, size)}
                title={`${t.name}, ${t.kind}, ${capacityLabel(t)}${hiddenNote(t)}`}
                aria-label={`${t.name}, ${capacityLabel(t)}. Drag it, or move it with the arrow keys; Delete takes it off the floor.`}
                onPointerDown={pickUp(t, false)}
                onPointerMove={carry}
                onPointerUp={putDown(t)}
                onPointerCancel={() => setDrag(null)}
                onKeyDown={nudge(t, spot)}
              >
                {face(t)}
              </button>
            );
          }
          const to = linkOf?.(t);
          return to ? (
            <Link
              key={t.id}
              to="/brews/$brewId"
              params={{ brewId: to }}
              className={classOf(t)}
              style={style(t, spot, size)}
              title={`${t.name}${hiddenNote(t)}: open its brew`}
            >
              {face(t)}
            </Link>
          ) : (
            <div
              key={t.id}
              className={classOf(t)}
              style={style(t, spot, size)}
              title={`${t.name}${hiddenNote(t)}`}
            >
              {face(t)}
            </div>
          );
        })}
        {dragged && drag?.fromTray && drag.moved ? (
          <div
            className={classOf(dragged, "lifted ghost")}
            style={style(dragged, drag.at, drag.size)}
            aria-hidden
          >
            {face(dragged)}
          </div>
        ) : null}
      </div>
    </div>
  );
}

const hiddenNote = (t: Tank) => (t.hidden ? ", hidden from the tank schedule" : "");
